import { useQuery } from '@tanstack/react-query';

import { Alert } from '../components/dashboard/AlertCard';
import { queryKeys } from '../lib/queryKeys';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export function useDashboardAlerts() {
  const { data: companyId, isLoading: isLoadingCompany } = useCurrentCompany();

  return useQuery<Alert[]>({
    queryKey: queryKeys.dashboard.alerts(companyId),
    enabled: !!companyId && !isLoadingCompany,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
    retry: 1, // Only retry once to avoid spamming errors
    queryFn: async () => {
      if (!companyId) {
        return [];
      }

      const alerts: Alert[] = [];

      // Check for late payments
      // payment_records doesn't have company_id directly - need to query through enrollments
      const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString();
      
      // Get enrollment IDs for this company
      const { data: enrollments } = await supabase
        .from('subscription_enrollments')
        .select('id')
        .eq('company_id', companyId);
      
      const enrollmentIds = enrollments?.map(e => e.id) || [];
      
      // Query payment_records through enrollments
      const { count: latePaymentsCount } = enrollmentIds.length > 0
        ? await supabase
            .from('payment_records')
            .select('id', { count: 'exact', head: true })
            .in('enrollment_id', enrollmentIds)
            .eq('status', 'pending')
            .lt('billing_date', threeDaysAgo)
        : { count: 0 };

      if (latePaymentsCount && latePaymentsCount > 0) {
        alerts.push({
          id: 'late-payments',
          type: 'error',
          title: 'Late Payments',
          message: `${latePaymentsCount} payment${latePaymentsCount > 1 ? 's' : ''} overdue by more than 3 days`,
          actionLabel: 'View Payments',
          actionPath: '/payments',
          count: latePaymentsCount,
        });
      }

      // Check for pending payments
      // payment_records doesn't have company_id directly - need to query through enrollments
      const { count: pendingPaymentsCount } = enrollmentIds.length > 0
        ? await supabase
            .from('payment_records')
            .select('id', { count: 'exact', head: true })
            .in('enrollment_id', enrollmentIds)
            .eq('status', 'pending')
        : { count: 0 };

      if (pendingPaymentsCount && pendingPaymentsCount > 0 && latePaymentsCount === 0) {
        alerts.push({
          id: 'pending-payments',
          type: 'warning',
          title: 'Pending Payments',
          message: `${pendingPaymentsCount} payment${pendingPaymentsCount > 1 ? 's' : ''} awaiting processing`,
          actionLabel: 'View Payments',
          actionPath: '/payments',
          count: pendingPaymentsCount,
        });
      }

      // Check for subscriptions pending cancellation
      const { count: pendingCancellationsCount } = await supabase
        .from('subscription_enrollments')
        .select('id', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .eq('status', 'pending_cancellation');

      if (pendingCancellationsCount && pendingCancellationsCount > 0) {
        alerts.push({
          id: 'pending-cancellations',
          type: 'warning',
          title: 'Pending Cancellations',
          message: `${pendingCancellationsCount} subscription${pendingCancellationsCount > 1 ? 's' : ''} scheduled for cancellation`,
          actionLabel: 'View Subscriptions',
          actionPath: '/subscriptions-plans/gym',
          count: pendingCancellationsCount,
        });
      }

      // Draft student metrics (uploaded, invited, accepted) are now shown in OnboardingDashboard
      // These are onboarding status metrics, not action items requiring attention
      // Removed from alerts - users can click KPI cards in OnboardingDashboard to filter AccountManager

      return alerts;
    },
  });
}
